import React, { Dispatch, Fragment, useEffect, useState } from 'react';
import Button from "react-bootstrap/Button";
import { useDispatch, useSelector } from 'react-redux';
import { changeSelectedItem, deleteItem, getItems, getSummaryItems } from '../../store/actions/timesheet.actions';
import { IState } from '../../store/models/IState';
import { ITimesheetItem } from '../../store/models/ITimesheetItem';
import { ITimesheetState } from '../../store/models/ITimesheetState';
import { ITimesheetSummaryItem } from '../../store/models/ITimesheetSummaryItem';
import { getAustraliaDateFormat } from '../../utils/utils';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import LoadingIndicator from '../common/LoadingIndicator';

export interface ITimesheetListProps {
    /**
     * Determines if you can edit the items.
     */
    isReadOnly: boolean;

    /**
     * Function to be called when an item is selected.
     */
    onSelected?: () => void;
}

/**
 * Displays a list of timesheet items.
 */
const TimesheetList: React.FC<ITimesheetListProps> = (props) => {
    const dispatch: Dispatch<any> = useDispatch();
    const timesheetState: ITimesheetState = useSelector((state: IState) => state.timesheetState);
    const selectedStartDate: Date = timesheetState.selectedStartDate;
    
    // Modals.    
    const [deleteConfirmationModalShow, setDeleteConfirmationModalShow] = useState(false);

    /**
     * Get the items when the start date changes.
     * Get the summary items when the start date changes.
     * Also triggered when the add/update modal is shown - see Timesheet component.
     */
    useEffect(() => {
        dispatch(getItems(selectedStartDate));        
    }, [dispatch, selectedStartDate]);

    /**
     * Get the summary items when the items or the start date changes.
     */
    useEffect(() => {
        dispatch(getSummaryItems(selectedStartDate));
    }, [
        dispatch,
        selectedStartDate,
        timesheetState.items,
        timesheetState.items.length
    ]);

    /**
     * If an item has been added or updated then refresh the summary items.
     */
    useEffect(() => {
        if (timesheetState.isPutItemSuccess) {
            dispatch(getSummaryItems(selectedStartDate));
        }
    }, [
        dispatch,
        selectedStartDate,
        timesheetState.isPutItemSuccess
    ])

    /**
     *  Close the confirmation modal.
     */ 
    function onCancelDeleteConfirmationModal(): void {
        setDeleteConfirmationModalShow(false);
    }

    /**
     * User has confirmed to delete the item so call delete action and close dialog.
     */
    function onConfirmDeletion(): void {
        if (!timesheetState.selectedItem) {
            return;
        }

        dispatch(deleteItem(timesheetState.selectedItem));
        setDeleteConfirmationModalShow(false);
    }

    /**
     * Update the selected item and open the delete confirmation.
     * @param item
     */
    function onDeleteButtonClick(item: ITimesheetItem): void {
        dispatch(changeSelectedItem(item));
        setDeleteConfirmationModalShow(true);
    }

    /**
     * Determines if the item is selected to highlight the row in the table.
     * @param item
     */
    function isItemSelected(item: ITimesheetItem): boolean {
        if (!timesheetState || !timesheetState?.selectedItem) {
            return false;
        }

        return (timesheetState?.selectedItem?.id === item.id);
    }

    /**
     * Updates the currently selected item. Calls the onSelected callback function so a modal dialog can be displayed.
     * @param item
     */
    function onTableRowClick(item: ITimesheetItem): void {
        console.log(`Item selected: ${getselectedItemName(item)}`);
        
        if (props.onSelected) {
            dispatch(changeSelectedItem(item));
            props.onSelected();
        }
    }

    /**
     * Display the action column if the list is not readonly. Displays a delete button.
     */
    function getActionColumnContent(index: number, employee: ITimesheetItem): JSX.Element | null {
        if (props.isReadOnly) {
            // No need for the column if readonly.
            return null;
        }

        return (
            <td>
                <div className="action-button-column text-center">
                    <Button key={index} variant="secondary" className="action-link-button link-button" id={`delete-${index}`} onClick={() => onDeleteButtonClick(employee)}><i className="fas fa-times-circle"></i></Button>
                </div>
            </td>
        )
    }

    /**
     * Display the summary items as a list of spans.
     */
    function getTimesheetSummaryItems(): JSX.Element | null {
        if (timesheetState.summaryItems.length === 0) {
            return null;
        };

        const summaryItems: JSX.Element[] = timesheetState.summaryItems.map((item: ITimesheetSummaryItem, index: number) => {
            return (
                <td key={index} className="summary-item text-center align-middle">
                    <span className="client-name">{item.client}</span>: {item.timeInHours} hrs
                </td>
            );
        });

        return (
            <div className="summary-items table-responsive shadow portlet">
                <table className="table table-striped">
                    <thead className="thead-dark">
                        <tr>
                            <th scope="col" colSpan={timesheetState.summaryItems.length}>
                                Summary
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="table-row">
                            {summaryItems}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    /**
     * Creates a table row for each item.
     */
    const getTimeSheetItemTableRows: (JSX.Element | null)[] = timesheetState?.items?.map((item: ITimesheetItem, index: number) => {
        if (!item) {
            return null;
        }

        return (
            <tr className={`table-row ${isItemSelected(item) ? "selected" : ""}`} key={`${item.id}`}>
                <td onClick={() => { onTableRowClick(item) }}>{getAustraliaDateFormat(item.date)}</td>
                <td onClick={() => { onTableRowClick(item) }}>{item.client}</td>
                <td onClick={() => { onTableRowClick(item) }}>{item.description}</td>
                <td onClick={() => { onTableRowClick(item) }}>{item.task}</td>
                <td onClick={() => { onTableRowClick(item) }}>{item.timeInHours}</td>
                {getActionColumnContent(index, item)}
            </tr>
        );
    });

    /**
     * Get the full name of the item to use to display in delete confirmation.
     */
    function getselectedItemName(item: ITimesheetItem | null): string {
        if (!item) {
            return '';
        }

        return `${item?.id} (${item?.date})`;
    }

    /**
     * Display the action column if the list is not readonly.
     */
    function getActionColumnHeaderContent(): JSX.Element | null {
        if (props.isReadOnly) {
            // No need for the column if readonly.
            return null;
        }

        return (
            <th scope="col" className="text-center align-middle">
                Delete
            </th>
        )
    }

    /**
     * Renders the table of items and any summary information.
     * @returns
     */
    function getContent(): JSX.Element | null {
        if (timesheetState?.isLoading) {
            return null;
        }

        return (
            <div>

                {getTimesheetSummaryItems()}

                <div className="table-responsive shadow portlet">
                    <table className="table table-striped">
                        <thead className="thead-dark">
                            <tr>
                                <th scope="col" className="align-middle">
                                    Date
                                </th>
                                <th scope="col" className="align-middle">
                                    Client
                                </th>
                                <th scope="col" className="align-middle">
                                    Description
                                </th>
                                <th scope="col" className="align-middle">
                                    Task
                                </th>
                                <th scope="col" className="align-middle">
                                    Hours
                                </th>
                                {getActionColumnHeaderContent()}
                            </tr>
                        </thead>
                        <tbody>
                            {getTimeSheetItemTableRows}
                        </tbody>
                    </table>

                    <ConfirmDeletionModal showConfirmationModal={deleteConfirmationModalShow} onCancel={onCancelDeleteConfirmationModal} onConfirm={onConfirmDeletion} itemName={""} size="lg"></ConfirmDeletionModal>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <LoadingIndicator isLoading={timesheetState?.isLoading} />
            {getContent()}
        </Fragment>
    );
}

export default TimesheetList